import { InvoiceSummary } from 'olo-service';
import React, { useCallback, useEffect, useState } from 'react';
import { useSafeContext, createNamedContext } from '@clover-util/react-context';
import { useCart } from 'hooks/context/cart';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { AxiosResponse } from 'axios';
import { useTableOrderCheckoutResource, usePostCheckoutResource } from 'hooks/useApi';
import { useMerchant } from 'hooks/context/merchant';
import { useServiceType } from 'hooks/context/serviceType';
import { usePersistentQuery } from 'hooks/context/PersistentQueryParam';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useGiftCard } from '../GiftCard/GiftCardContext';

type InvoiceSummaryState = {
  invoiceSummary?: InvoiceSummary;
  isInvoiceSummaryLoading: boolean;
  error?: AxiosResponse;
  selectedPerk?: string;
  setSelectedPerk?: (uuid?: string) => void;
  postOrderError?: boolean;
  setTipvalue: (state:number) => void;
  setPostOrder?: (state: boolean) => void;
};

const InvoiceSummaryContext = createNamedContext<InvoiceSummaryState>('InvoiceSummaryContext');
const InvoiceDineInSummaryContext = createNamedContext<InvoiceSummaryState>('InvoiceSummaryContext');

const DineInInvoiceProvider: React.FC = ({ children }) => {
  const { merchant } = useMerchant();
  const queryParams = usePersistentQuery();
  const [tipValue , setTipvalue] = useState<number>();
  const { appliedGiftCards } = useGiftCard();
  const { isInvoiceSummaryLoading, invoiceSummary, invoiceSummaryError } = useTableOrderCheckoutResource(merchant?.merchantUuid, queryParams?.table_id, tipValue,appliedGiftCards);

  return (
    <InvoiceDineInSummaryContext.Provider value={ { isInvoiceSummaryLoading, invoiceSummary , error:invoiceSummaryError, setTipvalue }}>
      {children}
    </InvoiceDineInSummaryContext.Provider>
  );
};

const InvoiceProvider: React.FC = ({ children }) => {
  const { merchant } = useMerchant();
  const { items: cartItems, subtotal: cartItemsSubtotal } = useCart();
  const { items: giftCards, subtotal: giftCardSubtotal } = useGiftCardCart();
  const [tipValue, setTipvalue] = useState<number>();
  const { appliedGiftCards } = useGiftCard();
  const { currentServiceType } = useServiceType();
  const [perk, setPerk] = useState<string>();
  const [postOrderError, setPostOrderError] = useState<boolean>(false);
  const { invoiceSummary, isInvoiceSummaryLoading, error } = usePostCheckoutResource(merchant?.merchantUuid, cartItems, giftCards, cartItemsSubtotal + giftCardSubtotal, currentServiceType, perk,appliedGiftCards,tipValue);

  // if checkout API is successful, then dont show 86th error from orders API
  useEffect(() => {
    if (invoiceSummary) setPostOrderError(false);
  }, [invoiceSummary]);

  useEffect(() => {
    if (isInvoiceSummaryLoading || !perk) return;
    if (invoiceSummary && !invoiceSummary?.applicablePerks?.includes(perk)) setPerk(undefined);
  }, [perk, isInvoiceSummaryLoading, invoiceSummary]);

  return (
    <InvoiceSummaryContext.Provider value={{
      selectedPerk: perk,
      setSelectedPerk: useCallback((uuid?:string) => {
        setPerk(uuid);
      }, [setPerk]),
      setPostOrder : useCallback((state: boolean) => {
        setPostOrderError(state);
      }, [setPostOrderError]),
      postOrderError,
      invoiceSummary,
      setTipvalue,
      isInvoiceSummaryLoading,
      error
      }}>
      {children}
    </InvoiceSummaryContext.Provider>
  );
};

export function useInvoiceSummary() {
  const { showRunningOrderState } = useMerchantState();

  return useSafeContext(useInvoiceSummary, showRunningOrderState ? InvoiceDineInSummaryContext : InvoiceSummaryContext);
}

export const InvoiceSummaryProvider: React.FC = ({ children }) => {
  const { showRunningOrderState } = useMerchantState();
  const Provider = showRunningOrderState ? DineInInvoiceProvider : InvoiceProvider;

  return (
    <Provider>{children}</Provider>
  );
};
